/**
 * ReimbursementCheckCard
 *
 * QA tool for verifying Medicare reimbursement values shown in Market Data.
 *
 * For a selected provider (or organization) + PX service line it:
 *   1. Fetches every code in the service line
 *   2. Recomputes reimbursement: SUM(COALESCE(claim_count, 5) × fee)
 *      - Provider: MedicarePhysicianFees (facility vs non-facility by location_type)
 *      - Org:      OPPS/ASC/CLFS facility schedules (wage-adjusted where applicable)
 *   3. Compares the recomputed value against the stored reimbursement_value
 *   4. Shows a per-code breakdown sorted by contribution
 *
 * DX service lines have no Medicare fee → always shows N/A.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  Icon,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import axiosInstance from 'utils/axiosUtil';
import { StoreUtil } from 'doctivity-shared/utils';
import { FindProviderDialog } from 'components';
import { FindOrganizationDialog } from 'organizations';

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  cardInnerHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    flex: 1,
  },
  entityToggle: {
    marginBottom: theme.spacing(1),
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      padding: '4px 14px',
      fontSize: 13,
    },
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
  },
  controlItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  controlLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  controlValue: {
    fontSize: 13,
  },
  servicelineSelect: {
    minWidth: 280,
  },
  slParent: {
    borderTop: '1px solid #cccccc',
  },
  slChild: {
    fontSize: 14,
    paddingLeft: theme.spacing(4),
  },
  slCustom: {
    fontSize: 14,
    paddingLeft: theme.spacing(4),
  },
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  summaryMatch: {
    backgroundColor: '#e8f5e9',
  },
  summaryMismatch: {
    backgroundColor: '#ffebee',
  },
  summaryNeutral: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 2,
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },
  summaryValueMismatch: {
    color: '#c62828',
  },
  summaryValueMatch: {
    color: '#2e7d32',
  },
  chipMatch: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600,
    fontSize: 12,
  },
  chipMismatch: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 600,
    fontSize: 12,
  },
  delta: {
    fontSize: 12,
    color: '#c62828',
    fontWeight: 600,
    marginTop: 2,
  },
  marketLink: {
    marginLeft: 'auto',
    fontSize: 13,
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  feeContext: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    fontFamily: 'monospace',
  },
  breakdownToggle: {
    fontSize: 12,
    color: '#1976d2',
    cursor: 'pointer',
    textDecoration: 'underline',
    userSelect: 'none',
    display: 'inline-block',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  breakdownTable: {
    marginTop: theme.spacing(1),
    '& th': {
      fontSize: 11,
      fontWeight: 700,
      padding: '4px 12px',
      backgroundColor: theme.palette.grey[100],
    },
    '& td': {
      fontSize: 12,
      padding: '3px 12px',
      fontFamily: 'monospace',
    },
  },
  noData: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  errorMsg: {
    fontSize: 13,
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
});

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtCurrency(n) {
  if (n === null || n === undefined) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

function slLabel(sl) {
  return sl.is_diagnosis != null
    ? `${sl.name} (${sl.is_diagnosis ? 'Dx' : 'Px'})`
    : sl.name;
}

/**
 * Strips explicit DX service lines from the list for the reimbursement dropdown.
 *
 * NOTE: raw SQL returns is_diagnosis as 0/1/null (MariaDB integers), not JS booleans.
 *   DX     → is_diagnosis = 1  ← remove these
 *   PX     → is_diagnosis = 0  ← keep
 *   parent → is_diagnosis = null ← keep (children not present in result set; can't infer)
 *
 * We only remove entries that are definitively DX. Parent service lines are always
 * kept — their children are not returned by the API query so we can't determine
 * child composition here, and they may well have PX sub-service lines.
 */
function filterPxServicelines(servicelines) {
  // Use loose equality (==) — MariaDB raw queries may return is_diagnosis as
  // integer 1, string "1", or boolean true depending on driver/version.
  // null == 1 is false, so parent rows (is_diagnosis = null) safely pass through.
  const isDx = (sl) => sl.is_diagnosis == 1; // eslint-disable-line eqeqeq
  return servicelines.filter((sl) => !isDx(sl));
}

function buildServicelineMenuItems(servicelines, classes) {
  const custom   = servicelines.filter((sl) => sl.client_id != null);
  const standard = servicelines.filter((sl) => sl.client_id == null);

  const parentMap = new Map();
  const childMap  = new Map();
  standard.forEach((sl) => {
    if (sl.parent_id == null) {
      parentMap.set(sl.id, sl);
    } else {
      if (!childMap.has(sl.parent_id)) childMap.set(sl.parent_id, []);
      childMap.get(sl.parent_id).push(sl);
    }
  });
  const orphans = standard.filter(
    (sl) => sl.parent_id != null && !parentMap.has(sl.parent_id)
  );

  const items = [];

  if (custom.length > 0) {
    items.push(
      <ListSubheader key='hdr-custom' style={{ lineHeight: '28px', fontSize: 11 }}>
        Custom Service Lines
      </ListSubheader>
    );
    custom.forEach((sl) => items.push(
      <MenuItem key={sl.id} value={sl.id} className={classes.slCustom}>{slLabel(sl)}</MenuItem>
    ));
    items.push(<Divider key='divider-custom' />);
  }

  parentMap.forEach((parent) => {
    items.push(
      <MenuItem key={parent.id} value={parent.id} className={classes.slParent}>{slLabel(parent)}</MenuItem>
    );
    (childMap.get(parent.id) || []).forEach((child) => {
      items.push(
        <MenuItem key={child.id} value={child.id} className={classes.slChild}>{slLabel(child)}</MenuItem>
      );
    });
  });

  orphans.forEach((sl) => items.push(
    <MenuItem key={sl.id} value={sl.id} className={classes.slParent}>{slLabel(sl)}</MenuItem>
  ));

  return items;
}

// ── Component ──────────────────────────────────────────────────────────────────

class ReimbursementCheckCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      entityType: 'provider',  // 'provider' | 'org'
      provider: null,
      providerDialogOpen: false,
      org: null,
      orgDialogOpen: false,
      servicelines: [],
      servicelineId: '',
      servicelineLoading: false,
      checking: false,
      result: null,
      error: null,
      breakdownOpen: false,
    };
  }

  componentDidUpdate(prev) {
    const { app } = this.props;
    if (app.selectedClient !== prev.app.selectedClient) {
      this.setState({ provider: null, org: null, servicelines: [], servicelineId: '', result: null });
    }
  }

  onEntityTypeChange = (_, newType) => {
    if (!newType) return;
    this.setState({
      entityType: newType,
      provider: null, org: null,
      servicelines: [], servicelineId: '', result: null, error: null, breakdownOpen: false,
    });
  };

  loadServicelines = async (entityType, entityId) => {
    this.setState({ servicelineLoading: true, servicelines: [], servicelineId: '', result: null });
    try {
      const isProvider = entityType === 'provider';
      const res = await axiosInstance.get(
        isProvider ? '/v2/qa/provider-servicelines' : '/v2/qa/org-servicelines',
        { params: isProvider ? { providerId: entityId } : { orgId: entityId } }
      );
      this.setState({ servicelines: res.data || [], servicelineLoading: false });
    } catch (err) {
      this.setState({ servicelineLoading: false, error: 'Failed to load service lines' });
    }
  };

  // ── Provider dialog ──────────────────────────────────────────────────────────

  openProviderDialog  = () => this.setState({ providerDialogOpen: true });
  closeProviderDialog = () => this.setState({ providerDialogOpen: false });
  onProviderSelected  = (provider) => {
    this.setState({ provider, providerDialogOpen: false });
    this.loadServicelines('provider', provider.id);
  };

  // ── Org dialog ───────────────────────────────────────────────────────────────

  openOrgDialog  = () => this.setState({ orgDialogOpen: true });
  closeOrgDialog = () => this.setState({ orgDialogOpen: false });
  onOrgSelected  = (org) => {
    if (!org) { this.setState({ orgDialogOpen: false }); return; }
    this.setState({ org, orgDialogOpen: false });
    this.loadServicelines('org', org.id);
  };

  // ── Run check ────────────────────────────────────────────────────────────────

  runCheck = async () => {
    const { entityType, provider, org, servicelineId } = this.state;
    const entity = entityType === 'provider' ? provider : org;
    if (!entity || !servicelineId) return;

    this.setState({ checking: true, error: null, result: null, breakdownOpen: false });
    try {
      const isProvider = entityType === 'provider';
      const res = await axiosInstance.get(
        isProvider ? '/v2/qa/reimbursement-check' : '/v2/qa/org-reimbursement-check',
        { params: isProvider
            ? { providerId: entity.id, servicelineId }
            : { orgId: entity.id, servicelineId }
        }
      );
      this.setState({ checking: false, result: res.data });
    } catch (err) {
      this.setState({
        checking: false,
        error: err.response?.data?.error || err.message || 'Check failed',
      });
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  renderFeeContext() {
    const { classes } = this.props;
    const { result, entityType } = this.state;
    if (!result?.feeContext) return null;

    const fc = result.feeContext;
    if (entityType === 'provider') {
      const parts = [
        fc.postal_code && `ZIP ${fc.postal_code}`,
        fc.carrier     && `carrier ${fc.carrier}`,
        fc.locality    && `locality ${fc.locality}`,
      ].filter(Boolean);
      if (parts.length === 0) return (
        <Typography className={classes.feeContext}>Fee schedule: no locality found for this provider</Typography>
      );
      return (
        <Typography className={classes.feeContext}>Fee schedule: {parts.join(' / ')}</Typography>
      );
    } else {
      const parts = [
        fc.fips_code  && `FIPS ${fc.fips_code}`,
        fc.cbsa       && `CBSA ${fc.cbsa}`,
        fc.wage_index != null
          ? `wage index ${Number(fc.wage_index).toFixed(4)}`
          : 'no wage index (using 1.0)',
      ].filter(Boolean);
      return (
        <Typography className={classes.feeContext}>Org fee context: {parts.join(' / ')}</Typography>
      );
    }
  }

  renderBreakdown() {
    const { classes } = this.props;
    const { result, breakdownOpen } = this.state;
    if (!result?.breakdown?.length) return null;

    return (
      <>
        <Typography
          className={classes.breakdownToggle}
          onClick={() => this.setState((s) => ({ breakdownOpen: !s.breakdownOpen }))}
        >
          {breakdownOpen ? '▾ Hide per-code breakdown' : '▸ Show per-code breakdown'} ({result.breakdown.length} codes)
        </Typography>
        <Collapse in={breakdownOpen}>
          <Table size='small' className={classes.breakdownTable}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell align='right'>Claims</TableCell>
                <TableCell align='right'>Reimbursement</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.breakdown.map((row) => (
                <TableRow key={row.code} hover>
                  <TableCell>{row.code}</TableCell>
                  <TableCell align='right'>{fmt(row.totalClaims)}</TableCell>
                  <TableCell align='right'>{fmtCurrency(row.contribution)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </>
    );
  }

  renderSummary() {
    const { classes } = this.props;
    const { result, entityType } = this.state;
    if (!result) return null;

    const { computedValue, storedValue, match, serviceline, codes, isDiagnosis } = result;
    const codeCount = Array.isArray(codes) ? codes.length : 0;
    const claimsType = entityType === 'provider' ? 'providers' : 'organizations';
    const marketDataUrl = `/claims?claimsType=${claimsType}&patientType=CAPTURED_CLAIMS&serviceline=${serviceline.id}`;

    // DX service lines never have reimbursement
    if (isDiagnosis) {
      return (
        <div className={`${classes.summary} ${classes.summaryNeutral}`}>
          <Typography style={{ fontSize: 13, color: '#999' }}>
            Diagnosis service lines have no Medicare reimbursement value — N/A.
          </Typography>
          <Link to={marketDataUrl} target='_blank' className={classes.marketLink}>
            View in Market Data ↗
          </Link>
        </div>
      );
    }

    const hasData     = computedValue !== null;
    const summaryClass = !hasData
      ? classes.summaryNeutral
      : match === true  ? classes.summaryMatch
      : match === false ? classes.summaryMismatch
      : classes.summaryNeutral;

    const valueClass = !hasData ? '' : match === false
      ? classes.summaryValueMismatch
      : match === true ? classes.summaryValueMatch
      : '';

    return (
      <>
        <div className={`${classes.summary} ${summaryClass}`}>
          {hasData && match !== null && (
            <Chip
              label={match ? '✓ Match' : '✗ Mismatch'}
              className={match ? classes.chipMatch : classes.chipMismatch}
            />
          )}

          <div className={classes.summaryItem}>
            <Typography className={classes.summaryLabel}>Codes in Service Line</Typography>
            <Typography className={classes.summaryValue}>
              {codeCount > 0 ? fmt(codeCount) : '—'}
            </Typography>
            {codeCount === 0 && (
              <Typography style={{ fontSize: 11, color: '#999' }}>Not mapped in system</Typography>
            )}
          </div>

          <div className={classes.summaryItem}>
            <Typography className={classes.summaryLabel}>Computed Value</Typography>
            <Typography className={`${classes.summaryValue} ${valueClass}`}>
              {hasData ? fmtCurrency(computedValue) : '—'}
            </Typography>
            {codeCount > 0 && !hasData && (
              <Typography style={{ fontSize: 11, color: '#999' }}>No PX code data for entity</Typography>
            )}
          </div>

          <div className={classes.summaryItem}>
            <Typography className={classes.summaryLabel}>Market Data Shows</Typography>
            <Typography className={`${classes.summaryValue} ${valueClass}`}>
              {storedValue !== null ? fmtCurrency(storedValue) : '—'}
            </Typography>
            {hasData && match === false && (
              <Typography className={classes.delta}>
                Δ {fmtCurrency(Math.abs(computedValue - storedValue))}
              </Typography>
            )}
            {hasData && match === null && (
              <Typography style={{ fontSize: 11, color: '#999' }}>No stored total found</Typography>
            )}
          </div>

          <Link to={marketDataUrl} target='_blank' className={classes.marketLink}>
            View in Market Data ↗
          </Link>
        </div>

        {this.renderFeeContext()}
        {this.renderBreakdown()}
      </>
    );
  }

  render() {
    const { classes, app, user } = this.props;
    const {
      entityType,
      provider,
      providerDialogOpen,
      org,
      orgDialogOpen,
      servicelines,
      servicelineId,
      servicelineLoading,
      checking,
      result,
      error,
    } = this.state;

    const isProvider = entityType === 'provider';
    const entity     = isProvider ? provider : org;
    const canRun     = !!entity && !!servicelineId && !checking;

    return (
      <Card className={classes.card} elevation={2}>
        <CardContent>
          {/* Header */}
          <div className={classes.cardInnerHeader}>
            <Typography component='h2' variant='h6' color='primary' gutterBottom className={classes.cardTitle}>
              Reimbursement Value Check
            </Typography>
            <Button
              color='primary'
              size='small'
              onClick={this.runCheck}
              disabled={!canRun}
              startIcon={
                checking
                  ? <CircularProgress size={16} color='inherit' />
                  : <Icon fontSize='small'>check_circle</Icon>
              }
            >
              {checking ? 'Checking…' : 'Run Check'}
            </Button>
          </div>

          {/* Entity type toggle */}
          <ToggleButtonGroup
            value={entityType}
            exclusive
            onChange={this.onEntityTypeChange}
            size='small'
            className={classes.entityToggle}
          >
            <ToggleButton value='provider'>Provider</ToggleButton>
            <ToggleButton value='org'>Organization</ToggleButton>
          </ToggleButtonGroup>

          {/* Controls */}
          <div className={classes.controls}>
            {/* Entity selector */}
            <div className={classes.controlItem}>
              <Typography className={classes.controlLabel}>
                {isProvider ? 'Provider:' : 'Organization:'}
              </Typography>
              {entity ? (
                <Typography className={classes.controlValue}>
                  {isProvider ? `${entity.first_name} ${entity.last_name}` : entity.name}
                </Typography>
              ) : (
                <Typography className={classes.controlValue} style={{ color: '#999' }}>
                  None selected
                </Typography>
              )}
              <Button
                size='small'
                color='primary'
                onClick={isProvider ? this.openProviderDialog : this.openOrgDialog}
                disabled={checking}
                style={{ marginLeft: 4, minWidth: 0, padding: '0 6px', fontSize: 11 }}
              >
                {entity ? 'Change' : 'Select'}
              </Button>
              {isProvider && provider && (
                <Link
                  to={`/providers/${provider.id}/codes`}
                  target='_blank'
                  style={{ marginLeft: 6, fontSize: 11 }}
                >
                  View Captured Claims ↗
                </Link>
              )}
            </div>

            {/* Service line */}
            <div className={classes.controlItem}>
              <FormControl size='small' className={classes.servicelineSelect}>
                <InputLabel>Select Service Line</InputLabel>
                <Select
                  value={servicelineId}
                  label='Select Service Line'
                  onChange={(e) => this.setState({ servicelineId: e.target.value, result: null, breakdownOpen: false })}
                  disabled={!entity || checking || servicelineLoading || filterPxServicelines(servicelines).length === 0}
                >
                  {servicelineLoading && <MenuItem disabled value=''>Loading…</MenuItem>}
                  {!servicelineLoading && !entity && (
                    <MenuItem disabled value=''>
                      {isProvider ? 'Select a provider first' : 'Select an organization first'}
                    </MenuItem>
                  )}
                  {!servicelineLoading && entity && filterPxServicelines(servicelines).length === 0 && (
                    <MenuItem disabled value=''>No Px service lines with claims found</MenuItem>
                  )}
                  {!servicelineLoading && entity && filterPxServicelines(servicelines).length > 0 &&
                    buildServicelineMenuItems(filterPxServicelines(servicelines), classes)
                  }
                </Select>
              </FormControl>
              {servicelineLoading && <CircularProgress size={18} />}
              <Typography style={{ fontSize: 11, color: '#999', marginLeft: 8, fontStyle: 'italic' }}>
                Only Px codes have possible reimbursement.
              </Typography>
            </div>
          </div>

          {/* Provider dialog */}
          <FindProviderDialog
            open={providerDialogOpen}
            onSelect={this.onProviderSelected}
            clientId={app.selectedClient}
            userId={user?.id}
            onClose={this.closeProviderDialog}
          />

          {/* Org dialog */}
          <FindOrganizationDialog
            open={orgDialogOpen}
            onSelect={this.onOrgSelected}
            clientId={app.selectedClient}
          />

          {/* Error */}
          {error && <Typography className={classes.errorMsg}>{error}</Typography>}

          {/* Summary + breakdown */}
          {result ? (
            this.renderSummary()
          ) : (
            !checking && (
              <Typography className={classes.noData}>
                Select a {isProvider ? 'provider' : 'organization'} and service line, then press Run Check.
              </Typography>
            )
          )}
        </CardContent>
      </Card>
    );
  }
}

ReimbursementCheckCard.propTypes = {
  classes: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const { app, user } = state;
  return { app, user: user || {} };
}

const styled = withStyles(styles)(ReimbursementCheckCard);
const connected = connect(mapStateToProps)(styled);
export { connected as ReimbursementCheckCard };
